import { useEffect, useState } from 'react';
import { API_BASE, getToken } from './api.js';
import { knownVoiceFlags, voiceFlags } from './voice.js';

/* ══════════════════════════════════════════════════════════════════════════
 * ZARYNA READS HER ANSWER OUT LOUD — Al-Noor, 22 Oct 2026.
 *
 * "yes, the microphone and the speaker… get it done." She speaks by herself
 * when the question was asked with the microphone (how ChatGPT and Gemini do
 * it), and a speaker button on every written answer plays it on demand. One
 * player for the whole app, so two answers never talk over each other. The
 * server decides the voice and the language (zaryna.speak.js); no key on the
 * server = no button.
 *
 * ⚠️ A PHONE ONLY PLAYS SOUND THAT FOLLOWS A TAP. Her answer arrives seconds
 * after the tap on the microphone, so the one shared player is "unlocked" on
 * the first tap anywhere (a silent play) and reused for every answer after.
 * ══════════════════════════════════════════════════════════════════════════ */

const player = typeof Audio !== 'undefined' ? new Audio() : null;
/* a real, tiny silent WAV made here (never a pasted file) — 0.05 s of quiet */
function silentUrl() {
  const n = 400, b = new ArrayBuffer(44 + n * 2), v = new DataView(b);
  const w = (o, t) => { for (let i = 0; i < t.length; i += 1) v.setUint8(o + i, t.charCodeAt(i)); };
  w(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, 8000, true); v.setUint32(28, 16000, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  w(36, 'data'); v.setUint32(40, n * 2, true);
  return URL.createObjectURL(new Blob([b], { type: 'audio/wav' }));
}
let unlocked = false;
if (typeof document !== 'undefined' && player) {
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    try { player.src = silentUrl(); player.play().catch(() => {}); } catch { /* old browser */ }
  };
  document.addEventListener('pointerdown', unlock, { once: true, capture: true });
  document.addEventListener('keydown', unlock, { once: true, capture: true });
}

const listeners = new Set();
let current = null;   // the text being read, or null
const announce = () => listeners.forEach((f) => f(current));

export function stopSpeaking() {
  if (player) { try { player.pause(); } catch { /* fine */ } }
  current = null; announce();
}

/* reads `text` aloud; resolves when it starts (or fails quietly — a voice
   that cannot play is never an error on screen, the words are already there) */
export async function speakAnswer(text, market) {
  if (!player || !text || !getToken()) return;
  stopSpeaking();
  current = text; announce();
  try {
    const r = await fetch(`${API_BASE}/zaryna/voice/speak`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ text, market: market || undefined }),
    });
    if (!r.ok || current !== text) { if (current === text) stopSpeaking(); return; }
    const url = URL.createObjectURL(await r.blob());
    if (current !== text) return;
    player.src = url;
    player.onended = () => { URL.revokeObjectURL(url); if (current === text) stopSpeaking(); };
    await player.play();
  } catch { if (current === text) stopSpeaking(); }
}

export function useSpeaking() {
  const [now, setNow] = useState(current);
  useEffect(() => { listeners.add(setNow); return () => listeners.delete(setNow); }, []);
  return now;
}

/* is her voice switched on (the server has the key)? */
export function useCanSpeak() {
  const [on, setOn] = useState(() => !!(knownVoiceFlags() && knownVoiceFlags().speak && knownVoiceFlags().speak.enabled));
  useEffect(() => {
    let live = true;
    voiceFlags().then((f) => { if (live) setOn(!!(f && f.speak && f.speak.enabled)); }).catch(() => {});
    return () => { live = false; };
  }, []);
  return on;
}

/* the speaker on a written answer: tap = read it, tap again = stop */
export function SpeakButton({ text, market }) {
  const can = useCanSpeak();
  const now = useSpeaking();
  if (!can || !text || !getToken()) return null;
  const on = now === text;
  return (
    <button type="button" className={'zspeak' + (on ? ' on' : '')}
      aria-label={on ? 'Stop' : 'Listen'} aria-pressed={on}
      onClick={() => (on ? stopSpeaking() : speakAnswer(text, market))}>
      {on
        ? <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" /></svg>
        : <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 5 6 9H3v6h3l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" />
          </svg>}
    </button>
  );
}
